import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuth,
  fetchApplicationAppId,
  PARTNER_TYPE,
  PartnerLogo,
} from "../../utils";

function PartnerCard({ partner, appId }) {
  const base = `/apps/${appId}/collaborate/partner-integrations/${partner.slug || partner.id}`;
  const integrated = Boolean(partner.is_integrated);
  return (
    <div className="pi-card">
      <div className="pi-card-head">
        <PartnerLogo partner={partner} className="pi-logo" />
        {integrated ? (
          <span className="pi-status pi-status--active">
            <span className="pi-status-dot" />
            Active
          </span>
        ) : null}
      </div>
      <h3 className="pi-card-name">{partner.name}</h3>
      <p className="pi-card-category">
        {partner.partner_type_label ||
          PARTNER_TYPE.find((t) => t.value === Number(partner.partner_type))?.label ||
          "Partner"}
      </p>
      <p className="pi-card-stat">{partner.stat_label || partner.description || ""}</p>
      <div className="pi-card-actions">
        <Link to={`${base}?tab=about`}>About</Link>
        <Link to={base}>{integrated ? "Manage" : "Integrate"}</Link>
      </div>
    </div>
  );
}

export default function CollaboratePartner() {
  const { appId: routeAppId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "marketplace" ? "marketplace" : "active";
  const [search, setSearch] = useState("");
  const [partnerType, setPartnerType] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [appUuid, setAppUuid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const uuid = await fetchApplicationAppId(routeAppId);
      if (!cancelled) setAppUuid(uuid || routeAppId);
    })();
    return () => {
      cancelled = true;
    };
  }, [routeAppId]);

  useEffect(() => {
    if (!appUuid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(Utils.baseurl + "get/partner-integrations", {
          method: "POST",
          body: JSON.stringify({
            app_id: appUuid,
            search,
            partner_type: partnerType || undefined,
          }),
        });
        const result = await response.json();
        if (cancelled) return;
        if (result.status) {
          setActive(result.data?.active || []);
          setMarketplace(result.data?.marketplace || []);
        } else {
          toast.error(result.message || "Failed to load integrations");
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          toast.error("Failed to load integrations");
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [appUuid, search, partnerType]);

  const list = useMemo(
    () => (tab === "active" ? active : marketplace),
    [tab, active, marketplace]
  );

  function setTab(next) {
    const params = new URLSearchParams(searchParams);
    if (next === "marketplace") params.set("tab", "marketplace");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="pi-page">
          <h1 className="pi-page-title">Partner Integrations</h1>

          <div className="pi-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "active"}
              className={`pi-tab ${tab === "active" ? "active" : ""}`}
              onClick={() => setTab("active")}
            >
              Active Integrations ({active.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "marketplace"}
              className={`pi-tab ${tab === "marketplace" ? "active" : ""}`}
              onClick={() => setTab("marketplace")}
            >
              Marketplace
            </button>
          </div>

          <div className="pi-toolbar">
            <input
              className="pi-search"
              type="search"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search integrations"
            />
            <select
              className="pi-filter-select"
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
              aria-label="Partner type"
            >
              <option value="">Partner type: All</option>
              {PARTNER_TYPE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="pi-empty">Loading…</div>
          ) : list.length ? (
            <div className="pi-cards">
              {list.map((partner) => (
                <PartnerCard key={partner.id} partner={partner} appId={routeAppId} />
              ))}
            </div>
          ) : (
            <div className="pi-empty">
              {tab === "marketplace"
                ? "No partners in marketplace. Ask admin to add partners."
                : "No active integrations yet. Open Marketplace and click Integrate."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
